import type { ObjectType } from '@/services/db/object-type/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlImportRelationship } from '@/features/sql-sync';
import type { SqlImportDraft } from './draft';

/** The relationship half of the draft, as the wizard edits it (the query is kept with it). */
export type DraftRelationship = NonNullable<SqlImportDraft['relationship']>;

/**
 * The relationship a new import starts with: people (entity 1) related to an object type
 * (entity 2). A people import relates to the first enabled object type; an object import relates
 * its objects back to people, so the identifiers swap sides.
 */
export function defaultRelationship(
  draft: Pick<SqlImportDraft, 'import_type' | 'id_field_name' | 'object_type_id'>,
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[],
): SqlImportRelationship {
  const isObjectImport = draft.import_type === ImportType.object;
  const firstEnabled = objectTypes.find((objectType) => objectType.enabled) ?? objectTypes[0];
  const parsed = Number(isObjectImport ? draft.object_type_id : firstEnabled?.id);
  const objectTypeId = Number.isFinite(parsed) ? parsed : 0;
  return {
    query: '',
    entity1_type_id: 0,
    entity1_id_field_name: isObjectImport ? 'id' : (draft.id_field_name ?? 'id'),
    entity2_type_id: objectTypeId,
    entity2_id_field_name: isObjectImport ? (draft.id_field_name ?? 'object_id') : 'object_id',
  };
}

/** The draft's relationship as it was stored, with defaults for its blanks. */
function storedRelationship(stored: DraftRelationship): SqlImportRelationship {
  return {
    query: stored.query ?? '',
    entity1_type_id: stored.entity1_type_id ?? 0,
    entity1_id_field_name: stored.entity1_id_field_name ?? 'id',
    entity2_type_id: stored.entity2_type_id ?? 0,
    entity2_id_field_name: stored.entity2_id_field_name ?? 'object_id',
  };
}

/**
 * The relationship the step works on: the draft's, or the default until something is chosen.
 *
 * The step only offers a control for the side the import does not fix: the people identifier of
 * an object import, the object type of a people import. The other sides come from Settings, so
 * they follow the draft rather than what the step stored, or a sync target changed after the
 * relationship was stored would still relate the rows to the old object type.
 */
export function relationshipForStep(
  draft: SqlImportDraft,
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[],
): SqlImportRelationship {
  const derived = defaultRelationship(draft, objectTypes);
  if (draft.relationship == null) {
    return derived;
  }
  const stored = storedRelationship(draft.relationship);
  const chosen =
    draft.import_type === ImportType.object
      ? { entity1_id_field_name: stored.entity1_id_field_name }
      : { entity2_type_id: stored.entity2_type_id };
  return { ...derived, ...chosen, query: stored.query, entity1_type_id: stored.entity1_type_id };
}

/**
 * The relationship to store when Settings moved a side of the import after the step stored it, or
 * null when the stored one still matches. An example query that was never edited follows the new
 * sides; an edited query is kept.
 */
export function resyncedRelationship(
  draft: SqlImportDraft,
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[],
  exampleQuery: (relationship: SqlImportRelationship) => string,
): SqlImportRelationship | null {
  if (draft.relationship == null) {
    return null;
  }
  const stored = storedRelationship(draft.relationship);
  const current = relationshipForStep(draft, objectTypes);
  const unchanged =
    stored.entity1_id_field_name === current.entity1_id_field_name &&
    stored.entity2_type_id === current.entity2_type_id &&
    stored.entity2_id_field_name === current.entity2_id_field_name;
  if (unchanged) {
    return null;
  }
  return stored.query === exampleQuery(stored) ? { ...current, query: exampleQuery(current) } : current;
}

type RelationshipCopy = { title: string; subtitle: string };

/**
 * The step's heading: which side relates to which. For a people import the objects are the target
 * and the query names object ids; for an object import the people are the target.
 */
export function relationshipCopy(
  isObjectImport: boolean,
  objectTypeName: string | undefined,
  people: { singular: string; plural: string },
): RelationshipCopy {
  const objects = objectTypeName ?? 'Objects';
  if (isObjectImport) {
    return {
      title: `How do these ${objects.toLowerCase()} relate to your people?`,
      subtitle: `Include an object_id column and either an id or email column for the related ${people.singular}.`,
    };
  }
  return {
    title: `How do these people relate to your ${objects.toLowerCase()}?`,
    subtitle: `For ${people.plural}, include either an id or an email. For the objects they should be related to, first select the appropriate object type in the dropdown, then include an object_id.`,
  };
}
